import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'

import { Acknowledge, Container, Stack } from '../../components'
import { mutations, queries } from '../../graphql'
import { useAuth } from '../../hooks'
import {
  AcceptSuccess,
  AcceptError,
  AcceptLoading,
  AdvisorDetailsForm,
  useAdvisorAcceptanceGate,
} from './advisor-connection-shared'

export function AcceptConnection() {
  const { token } = useParams()
  const [searchParams] = useSearchParams()
  const confirmed = searchParams.get('confirmed') === '1'
  const [{ isLoggedIn }, { logout }] = useAuth()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState(null)
  const [nameSaved, setNameSaved] = useState(false)
  const hasCalledRef = useRef(false)

  const {
    data: contextData,
    loading: contextLoading,
    error: contextError,
  } = useQuery(queries.onboarding.advisorInviteContext, { variables: { token }, skip: !token })
  const targetEmail = contextData?.advisorInviteContext?.targetEmail

  const {
    me,
    loading: meLoading,
    formRequired,
    advisorFundProfileId,
    existingSettings,
    suggestedCustodianName,
  } = useAdvisorAcceptanceGate({ skip: !confirmed || !isLoggedIn })

  const [acceptConnection, { data, error }] = useMutation(mutations.onboarding.acceptAdvisorConnection, {
    onError: (_error) => {
      setErrorMessage(_error.message)
    },
  })

  useEffect(() => {
    if (!token || contextLoading) return
    if (contextError) return

    if (!confirmed) {
      if (isLoggedIn) logout({ skipNavigation: true })
      navigate('/login', {
        state: {
          afterLogin: `/advisor/accept/${token}?confirmed=1`,
          ...(targetEmail && { prefillEmail: targetEmail }),
        },
      })
      return
    }

    if (!isLoggedIn) {
      navigate('/login', { state: { afterLogin: `/advisor/accept/${token}?confirmed=1` } })
      return
    }

    if (meLoading) return
    if (formRequired && !advisorFundProfileId) return
    if (formRequired && !nameSaved) return

    if (hasCalledRef.current) return
    hasCalledRef.current = true

    acceptConnection({ variables: { token } })
  }, [
    token,
    confirmed,
    isLoggedIn,
    contextLoading,
    contextError,
    targetEmail,
    logout,
    acceptConnection,
    navigate,
    meLoading,
    formRequired,
    advisorFundProfileId,
    nameSaved,
  ])

  if (contextError) {
    return (
      <Container maxWidth="lg">
        <Stack justifyItems="center">
          <AcceptError message="This invitation link is invalid or expired." />
        </Stack>
      </Container>
    )
  }

  if (!confirmed || !isLoggedIn) return null

  if (meLoading) {
    return (
      <Container maxWidth="lg">
        <Stack justifyItems="center">
          <AcceptLoading />
        </Stack>
      </Container>
    )
  }

  if (formRequired && !advisorFundProfileId) {
    return (
      <Container maxWidth="lg">
        <Stack justifyItems="center">
          <AcceptLoading />
        </Stack>
      </Container>
    )
  }

  if (formRequired && !nameSaved) {
    return (
      <Container maxWidth="lg">
        <Stack justifyItems="center">
          <AdvisorDetailsForm
            me={me}
            existingSettings={existingSettings}
            suggestedCustodianName={suggestedCustodianName}
            advisorFundProfileId={advisorFundProfileId}
            onSave={() => setNameSaved(true)}
          />
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      <Stack justifyItems="center">
        <Acknowledge.Switch
          success={!!data && <AcceptSuccess />}
          error={!!error && <AcceptError message={errorMessage} />}
        >
          <AcceptLoading />
        </Acknowledge.Switch>
      </Stack>
    </Container>
  )
}
